import { useCallback } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { get, isEmpty, isNull, uniqueId } from 'lodash';
import { FormControl, Grid, TextField } from '@material-ui/core';
import { translate } from 'containers/services/translate';
import messages from 'containers/CourtManagerLegalProceedings/Provision/Regulation/messages';
import { Listings } from 'containers/AppContainer/typings';
import SahamAccordion from 'components/SahamAccordion';
import {
  StyledContent,
  StyledData,
} from 'containers/CourtManagerLegalProceedings/Provision/styled-components';
import {
  getBeneficiaryRecipient,
  getCityByCode,
  getCountryByCode,
  getPaymentChannel,
  getPaymentMethod,
  getProcessNature,
  getRealBeneficiary,
  getTotalAmountByRisk,
  numberWithCommas,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';
import {
  Field,
  Title,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/styled-components';
import {
  REGULATIONS,
  REGULATION_AMOUNTS,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import { RISK_NAME } from 'containers/CourtManagerLegalProceedings/Provision/constants';
import {
  REGULATION_LEVEL_REQUIREMENT,
  REGULATION_STATUS,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import { EXPAND_RISK } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/constants';

export interface MaterialRegulationRecapProps {
  intl: IntlShape;
  setRiskRecap: Function;
  riskRecap: any;
  listings: Listings;
  activeStep: number;
  setActiveStep: Function;
  risk: any;
  expanded: any;
  handleChangeExpanded: Function;
  materialEncryptedDocuments: any;
  sagisClaim: any;
}

export const MaterialRegulationRecap: React.FC<MaterialRegulationRecapProps> = ({
  setRiskRecap,
  riskRecap,
  listings,
  setActiveStep,
  risk,
  activeStep,
  expanded,
  handleChangeExpanded,
  materialEncryptedDocuments,
  sagisClaim,
  ...props
}): JSX.Element => {
  const handleChange = useCallback(
    (item: any) => (
      event: React.ChangeEvent<{ name?: string; value: any }>,
    ) => {
      const risks = [...riskRecap];
      const currentRisk =
        risks.find((el: any) => el.riskName === risk.riskName) || {};

      const currentData =
        get(currentRisk, 'data', []).find((el: any) => el.id === item.id) || {};

      currentData[REGULATION_AMOUNTS.AMOUNT] = numberWithCommas(
        event.target.value,
      );

      setRiskRecap(risks);
    },
    [riskRecap],
  );

  return (
    <SahamAccordion
      key={risk.regulationId}
      title={risk.riskName}
      expanded={get(expanded[risk.regulationId], [EXPAND_RISK.SHOW], false)}
      onChange={handleChangeExpanded(risk, [EXPAND_RISK.SHOW])}
    >
      <div>
        <SahamAccordion
          key={risk.regulationId}
          title={translate(messages.judgementInformation, props)}
          expanded={get(expanded[risk.regulationId], [EXPAND_RISK.INFO], false)}
          accordionDetailsClassName="recap-details"
          onChange={handleChangeExpanded(risk, [EXPAND_RISK.INFO])}
        >
          <Grid container spacing={1} direction="row">
            {risk.riskName === RISK_NAME.MATERIEL && (
              <>
                <Grid item xs={4}>
                  <Title> {translate(messages.processNature, props)}</Title>
                  <Field>{getProcessNature(listings, risk)}</Field>
                </Grid>
                {isEmpty(
                  get(
                    risk.regulationInfo,
                    [REGULATIONS.JUDGEMENT_DATE_JAF],
                    '',
                  ),
                ) && (
                  <Grid item xs={4}>
                    <Title>
                      {get(risk.regulationInfo, [REGULATIONS.IS_APPEAL], '')
                        ? translate(messages.appealDate, props)
                        : translate(messages.judgementDate, props)}
                    </Title>
                    <Field>
                      {get(
                        risk.regulationInfo,
                        [REGULATIONS.JUDGEMENT_DATE],
                        null,
                      )}
                    </Field>
                  </Grid>
                )}
                {!isEmpty(
                  get(
                    risk.regulationInfo,
                    [REGULATIONS.JUDGEMENT_DATE_JAF],
                    '',
                  ),
                ) && (
                  <>
                    <Grid item xs={4}>
                      <Title>{translate(messages.appealDate, props)}</Title>
                      <Field>
                        {get(
                          risk.regulationInfo,
                          [REGULATIONS.JUDGEMENT_DATE],
                          null,
                        )}
                      </Field>
                    </Grid>{' '}
                    <Grid item xs={4}>
                      <Title>{translate(messages.judgementDate, props)}</Title>
                      <Field>
                        {get(
                          risk.regulationInfo,
                          [REGULATIONS.JUDGEMENT_DATE_JAF],
                          null,
                        )}
                      </Field>
                    </Grid>
                  </>
                )}

                <Grid item xs={4}>
                  <Title> {translate(messages.courtNumber, props)}</Title>
                  <Field>{get(risk, 'regulationInfo.courtNumber', '')}</Field>
                </Grid>
              </>
            )}
            <Grid item xs={4}>
              <Title>{translate(messages.recipient, props)}</Title>
              <Field>
                {getBeneficiaryRecipient(
                  risk,
                  get(listings, 'EncryptDocumentCity', []),
                )}
              </Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.realBeneficiary, props)}</Title>
              <Field>
                {getRealBeneficiary(
                  risk,
                  get(listings, 'EncryptDocumentCity', []),
                )}
              </Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.additionalAddress, props)}</Title>
              <Field>
                {get(risk.regulationInfo, [REGULATIONS.ADDITIONAL_ADDRESS], '')}
              </Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.beneficiaryReference, props)}</Title>
              <Field>
                {get(
                  risk.regulationInfo,
                  [REGULATIONS.BENEFICIARY_REFERENCE],
                  '',
                )}
              </Field>
            </Grid>
            {risk.riskName !== RISK_NAME.CORPOREAL && (
              <Grid item xs={4}>
                <Title>{translate(messages.linkToBeneficiary, props)}</Title>
                <Field>
                  {get(
                    risk.regulationInfo,
                    [REGULATIONS.LINK_TO_BENEFICIARY],
                    '',
                  )}
                </Field>
              </Grid>
            )}
            <Grid item xs={4}>
              <Title>{translate(messages.country, props)}</Title>
              <Field>{getCountryByCode(listings)}</Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.town, props)}</Title>
              <Field>{getCityByCode(risk, listings)}</Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.status, props)}</Title>
              <Field>{REGULATION_STATUS}</Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.levelRequirement, props)}</Title>
              <Field>{REGULATION_LEVEL_REQUIREMENT}</Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.paymentChannel, props)}</Title>
              <Field>{getPaymentChannel(listings, risk)}</Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.paymentMethod, props)}</Title>
              <Field>{getPaymentMethod(risk, listings)}</Field>
            </Grid>
          </Grid>
        </SahamAccordion>
        <SahamAccordion
          key={risk.regulationId}
          title={
            risk.riskName === RISK_NAME.MATERIEL
              ? translate(messages.vehicleInformationTitle, props)
              : translate(messages.additionalGarentiesTitle, props)
          }
          expanded={get(expanded[risk.regulationId], [EXPAND_RISK.SHOW], false)}
          onChange={handleChangeExpanded(risk, [EXPAND_RISK.SHOW])}
        >
          <StyledContent>
            {get(risk, 'data', []).map((riskData: any) => {
              return (
                <div>
                  {risk.riskName === RISK_NAME.MATERIEL && (
                    <StyledData key={get(riskData, 'id', uniqueId())}>
                      {!isEmpty(riskData?.registrationNumber)
                        ? get(riskData, 'registrationNumber', '')
                        : `${riskData?.lastName} ${riskData?.firstName}`}
                    </StyledData>
                  )}
                  {risk.riskName === RISK_NAME.GARANTIES_ANNEXES && (
                    <StyledData key={get(riskData, 'id', uniqueId())}>
                      {get(riskData, 'id', '')}
                    </StyledData>
                  )}
                  <Grid container spacing={1} direction="row">
                    {risk.riskName === RISK_NAME.MATERIEL && (
                      <>
                        {get(
                          risk.regulationInfo,
                          [REGULATIONS.IS_APPEAL],
                          '',
                        ) && (
                          <Grid item xs={3}>
                            <Title>
                              {translate(messages.appealDamageAmount, props)}
                            </Title>
                            <Field>
                              {numberWithCommas(
                                String(
                                  get(
                                    riskData.regulationAmount,
                                    [REGULATION_AMOUNTS.APPEAL_DAMAGE_AMOUNT],
                                    '',
                                  ),
                                ),
                              )}
                            </Field>
                          </Grid>
                        )}
                        {!isNull(
                          get(
                            riskData.regulationAmount,
                            [REGULATION_AMOUNTS.DAMAGE_AMOUNT],
                            null,
                          ),
                        ) && (
                          <Grid item xs={3}>
                            <Title>
                              {translate(messages.judgementDamageAmount, props)}
                            </Title>
                            <Field>
                              {numberWithCommas(
                                String(
                                  get(
                                    riskData.regulationAmount,
                                    [REGULATION_AMOUNTS.DAMAGE_AMOUNT],
                                    '',
                                  ),
                                ),
                              )}
                            </Field>
                          </Grid>
                        )}
                      </>
                    )}

                    <Grid item xs={6}>
                      <Title>
                        {translate(messages.principalAmount, props)}
                      </Title>
                      <Field>
                        {get(
                          riskData.regulationAmount,
                          [REGULATION_AMOUNTS.PRINCIPAL_AMOUNT],
                          '',
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={2}>
                      <FormControl fullWidth>
                        <TextField
                          fullWidth
                          label={translate(messages.totalAmount, props)}
                          onChange={handleChange(riskData)}
                          data-testid={REGULATION_AMOUNTS.AMOUNT}
                          value={
                            get(riskData, REGULATION_AMOUNTS.AMOUNT)
                              ? get(riskData, REGULATION_AMOUNTS.AMOUNT, '')
                              : getTotalAmountByRisk(
                                  risk,
                                  riskData,
                                  sagisClaim,
                                  materialEncryptedDocuments,
                                )
                          }
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </div>
              );
            })}
          </StyledContent>
        </SahamAccordion>
      </div>
    </SahamAccordion>
  );
};

export default injectIntl(MaterialRegulationRecap);
